import { escapeCsvCell } from "@/lib/csv";

// Serialize the selected, resolved presentation. In particular entity sorting,
// assignment filtering and personal columns have already been applied here;
// neither exporter classifies facts or adds up repeated Overview result rows.
function exportText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent?.trim() ?? "";
  if (!(node instanceof Element)) return "";
  if (node.matches("[data-export-controls], svg, img")) return "";
  const explicit = node.getAttribute("data-export-value");
  if (explicit !== null) return explicit;
  if (node instanceof HTMLInputElement) return node.value;
  return [...node.childNodes]
    .flatMap((child) => {
      const text = exportText(child);
      return text ? [text] : [];
    })
    .join(" ");
}

export function captureAnalyticsCsv(node: HTMLElement): string {
  const rows: string[][] = [];
  for (const context of node.querySelectorAll("[data-export-header], [data-export-note]"))
    rows.push([exportText(context)]);
  for (const table of node.querySelectorAll("table")) {
    if (table.closest("[data-export-controls]")) continue;
    for (const row of table.rows) {
      rows.push(
        [...row.cells].map((cell) => {
          const text = exportText(cell);
          const notes = [...cell.querySelectorAll("[data-export-annotation], [title]")].flatMap(
            (item) => {
              const note =
                item.getAttribute("data-export-annotation") ??
                (item.textContent?.trim() === "—" ? item.getAttribute("title") : null);
              return note ? [note] : [];
            },
          );
          if (cell.dataset.exportAnnotation) notes.push(cell.dataset.exportAnnotation);
          if (text === "—" && cell.title) notes.push(cell.title);
          return notes.length ? `${text} (${[...new Set(notes)].join("; ")})` : text;
        }),
      );
    }
  }
  // A selected creative detail is a non-tabular report section.
  if (!node.querySelector("table")) {
    for (const section of node.querySelectorAll("[data-export-content]"))
      rows.push([exportText(section)]);
  }
  return rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
}

// Renders the dashboard to a PNG on a white background at 2x, sharp enough to
// paste into Slack. The dashboard keeps its toolbar usable on screen, but
// exports show the branded context header and never capture interactive
// controls.
const BLANK_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

export async function captureAnalyticsPng(node: HTMLElement): Promise<Blob> {
  const { toPng } = await import("html-to-image");
  const scrollers = [...node.querySelectorAll<HTMLElement>("[data-export-scroll]")];
  const controls = [...node.querySelectorAll<HTMLElement>("[data-export-controls]")];
  const headers = [...node.querySelectorAll<HTMLElement>("[data-export-header]")];
  const restore = {
    nodeWidth: node.style.width,
    nodeOverflow: node.style.overflow,
    nodePadding: node.style.padding,
    scrollers: scrollers.map((scroller) => ({
      element: scroller,
      overflow: scroller.style.overflow,
      maxHeight: scroller.style.maxHeight,
      scrollLeft: scroller.scrollLeft,
      scrollTop: scroller.scrollTop,
    })),
    controls: controls.map((control) => ({ element: control, display: control.style.display })),
    headers: headers.map((header) => ({ element: header, display: header.style.display })),
  };

  try {
    for (const control of controls) control.style.display = "none";
    for (const header of headers) header.style.display = "flex";
    for (const scroller of scrollers) {
      scroller.scrollLeft = 0;
      scroller.scrollTop = 0;
      scroller.style.overflow = "visible";
      // A scroller capped to the viewport (DataGrid) exports at full height.
      scroller.style.maxHeight = "none";
    }
    node.style.overflow = "visible";
    node.style.padding = "24px";
    const contentWidth = Math.max(
      node.scrollWidth,
      ...scrollers.map((item) => item.scrollWidth + 48),
    );
    node.style.width = `${contentWidth}px`;
    const dataUrl = await toPng(node, {
      backgroundColor: "#ffffff",
      pixelRatio: 2,
      // The UI uses a system fallback when Inter is unavailable. Avoid
      // html-to-image walking cross-origin stylesheet rules, which browsers
      // block and which previously made otherwise valid exports fail.
      skipFonts: true,
      // An image the browser will not hand over (CORS, an expired link) becomes
      // a blank space instead of failing the whole export.
      imagePlaceholder: BLANK_PIXEL,
    });
    const response = await fetch(dataUrl);
    if (!response.ok) throw new Error("Could not create the PNG image.");
    return await response.blob();
  } finally {
    node.style.width = restore.nodeWidth;
    node.style.overflow = restore.nodeOverflow;
    node.style.padding = restore.nodePadding;
    for (const item of restore.scrollers) {
      item.element.style.overflow = item.overflow;
      item.element.style.maxHeight = item.maxHeight;
      item.element.scrollLeft = item.scrollLeft;
      item.element.scrollTop = item.scrollTop;
    }
    for (const item of restore.controls) item.element.style.display = item.display;
    for (const item of restore.headers) item.element.style.display = item.display;
  }
}
